import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import Select from 'react-select';
import {
  FiPackage,
  FiSave
} from 'react-icons/fi';
import { MdDelete, MdOutlineAssignmentReturn } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import LeftMenu from '../components/LeftMenu';
import { BASE_URL, GET_INVOICE_ITEMS, GET_SUPPLIER_MASTER, POST_PURCHASE_RETURN_DRAFT } from '../utlilities/config';

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    minHeight: '32px',
    height: '32px',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px',
    height: '32px',
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px',
    padding: '0px',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '32px',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
};

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

// Batch Selection Modal Component
const BatchSelectionModal = ({ isOpen, onClose, product, batches, onSelectBatches, initialBatchQtys = {} }) => {
  const [batchQtys, setBatchQtys] = useState({});
  const modalRef = useRef(null);
  const selectButtonRef = useRef(null);
  const qtyInputRefs = useRef([]);

  useEffect(() => {
    if (isOpen && batches.length > 0) {
      const initialQtys = {};
      batches.forEach((batch, index) => {
        const presetQty = initialBatchQtys[batch.batchNo];
        initialQtys[index] = presetQty !== undefined && presetQty !== null && presetQty !== ''
          ? String(presetQty)
          : '';
      });
      setBatchQtys(initialQtys);
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
        qtyInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen, batches, initialBatchQtys]);

  const handleQtyChange = (index, value) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setBatchQtys((prev) => ({ ...prev, [index]: value }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'Enter' && e.target.tagName !== 'INPUT') {
      e.preventDefault();
      handleSelectBatches();
    }
  };

  const handleSelectBatches = () => {
    const selections = batches
      .map((batch, index) => ({
        batch,
        qty: parseFloat(batchQtys[index] || 0),
      }))
      .filter((item) => item.qty > 0);

    if (selections.length === 0) {
      toast.error('Please enter quantity for at least one batch', { autoClose: 1500 });
      return;
    }

    const invalidSelection = selections.find(
      (item) => item.qty > parseFloat(item.batch.availableQty || 0)
    );
    if (invalidSelection) {
      toast.error(
        `Quantity exceeds available stock for batch ${invalidSelection.batch.batchNo}. Max: ${invalidSelection.batch.availableQty || 0}`,
        { autoClose: 2500 }
      );
      return;
    }

    onSelectBatches(selections);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = batches.filter((_, index) => parseFloat(batchQtys[index] || 0) > 0).length;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div 
        className="bg-white rounded-lg p-6 w-[680px] max-h-[80vh] overflow-y-auto outline-none"
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <h3 className="text-lg font-bold mb-1">Select Batch - {product?.name}</h3>
        <p className="text-xs text-gray-500 mb-4">Enter return quantity for one or more batches</p>
        
        <div className="space-y-3 mb-6">
          {batches.map((batch, index) => (
            <div 
              key={`${batch.batchNo}-${index}`}
              className="border-2 border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className="font-semibold text-sm text-gray-700">Batch No:</span>
                      <span className="ml-2 font-bold text-sm text-black">{batch.batchNo || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
                        Available: {batch.availableQty || 0}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs text-black">
                    <div>
                      <span className="font-medium text-gray-700">Supplier:</span>
                      <span className="ml-2">{batch.supplierName || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Purchase Ref:</span>
                      <span className="ml-2">{batch.purchaseRefNo || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="w-24 shrink-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1 text-center">Qty</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    className="w-full px-2 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    value={batchQtys[index] ?? ''}
                    onChange={(e) => handleQtyChange(index, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    ref={(el) => { qtyInputRefs.current[index] = el; }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (index < batches.length - 1) {
                          qtyInputRefs.current[index + 1]?.focus();
                        } else {
                          handleSelectBatches();
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="flex gap-3">
          <button
            type="button"
            className="flex-1 py-2 px-4 bg-gray-200 text-black rounded-lg hover:bg-gray-300"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            ref={selectButtonRef}
            className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            onClick={handleSelectBatches}
            disabled={selectedCount === 0}
          >
            Add {selectedCount > 0 ? `${selectedCount} Batch${selectedCount > 1 ? 'es' : ''}` : 'Batches'}
          </button>
        </div>
      </div>
    </div>
  );
};


const PurchaseReturn = () => {
  const [loading, setLoading] = useState(false);
  const [returnNumber, setReturnNumber] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [note, setNote] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Medicine search states
  const [medicines, setMedicines] = useState([]);
  const [medicineSearch, setMedicineSearch] = useState('');
  const [showMedicineList, setShowMedicineList] = useState(false);
  const [selectedMedicineIndex, setSelectedMedicineIndex] = useState(-1);
  const [editingMedicineRowIndex, setEditingMedicineRowIndex] = useState(null);
  const [medicineSearchForEdit, setMedicineSearchForEdit] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Helper to calculate dropdown position — flip above when space below is tight
  const calculateDropdownPosition = (element) => {
    if (!element) return { top: 0, left: 0, width: 0 };
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const estimatedHeight = 350;

    if (spaceBelow < estimatedHeight && rect.top > spaceBelow) {
      return {
        bottom: viewportHeight - rect.top + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 600),
        isAbove: true,
      };
    }

    return {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 600),
      isAbove: false,
    };
  };
  
  // Batch selection states
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedMedicineForBatch, setSelectedMedicineForBatch] = useState(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);
  const [batchModalInitialQtys, setBatchModalInitialQtys] = useState({});
  const [suppliers, setSuppliers] = useState([]);

  const token = localStorage.getItem('accessToken');
  const noteRef = useRef(null);
  const productRefs = useRef([]);
  const expiryRefs = useRef([]);
  const qtyRefs = useRef([]);
  const submitBtnRef = useRef(null);

  // Helper function to parse decimal values (3 decimal places)
  const parseDecimal = (value) => parseFloat(parseFloat(value || 0).toFixed(3));

  const supplierOptions = suppliers.map((supplier) => ({
    value: supplier.SupplierID,
    label: supplier.SupplierName,
  }));

  const getSuppliers = async () => {
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'FilterText': '',
        },
      });

      if (response.status === 200) {
        setSuppliers(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const handleSupplierChange = (index, selectedOption) => {
    setProducts((prevProducts) =>
      prevProducts.map((product, i) =>
        i === index
          ? {
              ...product,
              supplierName: selectedOption ? selectedOption.label : '',
              supplierId: selectedOption ? selectedOption.value : 0,
            }
          : product
      )
    );
  };

  // Medicine search refs
  const medicineSearchInputRef = useRef(null);
  const medicineSearchContainerRef = useRef(null);
  const medicineDropdownRef = useRef(null);
  const medicineRowRefs = useRef([]);
  const medicineSearchDebounceRef = useRef(null);

  // Get invoice items (medicines) on search
  const getInvoiceItems = async (filterText) => {
    if (!filterText || filterText.length < 1) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_INVOICE_ITEMS}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        },
        params: {
          FilterText: filterText,
          ItemType: 'Pharmacy',
          FltrType: 'all',
          departmentID: localStorage.getItem('department_id') || ''
        }
      });

      if (response.status === 200) {
        const items = response.data.data || [];
        
        // Transform API response to medicine format
        const transformedMedicines = items.map((item) => {
          // Transform batches
          const batches = (item.itemBatch || []).map((batch) => ({
            batchNo: batch.batchCode || '',
            expiryDate: batch.expiryDate || '',
            mrp: parseFloat(batch.salesPrice || 0),
            rate: parseFloat(batch.salesPrice || 0),
            gstRate: parseFloat(item.taxCode || 0),
            availableQty: parseFloat(batch.onhand || 0),
            purchasePrice: parseFloat(batch.purchasePrice || 0),
            supplierName: batch.supplierName || '',
            supplierId: batch.supplier || batch.supplierId || batch.supplierID || 0,
            purchaseRefNo: batch.purchaseRefNo || '',
          }));

          return {
            id: item.item_No,
            name: item.item_Name || '',
            genericName: item.chemical || '',
            manufacturer: item.company || '',
            batches: batches,
            section: item.section || '',
            hsnCode: item.hsnCode || '',
            taxCode: item.taxCode || '',
            discount1: item.discount1 || 0,
            discount2: item.discount2 || 0,
            discount3: item.discount3 || 0,
            itemStock: item.itemStock || 0
          };
        });

        // Update medicines state with transformed data
        setMedicines(transformedMedicines);
      }
    } catch (error) {
      console.error('Failed to fetch invoice items:', error);
      toast.error('Failed to fetch products.', {
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const now = new Date();
    const formattedDateTime = `${String(now.getDate()).padStart(2, '0')}-${String(
      now.getMonth() + 1
    ).padStart(2, '0')}-${now.getFullYear()} ${now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })}`;

    setReturnNumber(generateReturnNumber(now));
    setReturnDate(formattedDateTime);
    getSuppliers();

    setTimeout(() => {
      if (medicineSearchInputRef.current) {
        medicineSearchInputRef.current.focus();
      }
    }, 200);
  }, []);

  useEffect(() => {
    productRefs.current = productRefs.current.slice(0, products.length);
    expiryRefs.current = expiryRefs.current.slice(0, products.length);
    qtyRefs.current = qtyRefs.current.slice(0, products.length);
  }, [products]);

  // Update dropdown position on scroll and resize
  useEffect(() => {
    if (!showMedicineList && editingMedicineRowIndex === null) return;

    const updatePosition = () => {
      const anchor =
        editingMedicineRowIndex !== null && productRefs.current[editingMedicineRowIndex]
          ? productRefs.current[editingMedicineRowIndex]
          : medicineSearchContainerRef.current;
      if (anchor) {
        setDropdownPosition(calculateDropdownPosition(anchor));
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showMedicineList, editingMedicineRowIndex]);

  // Calculate match score for relevance ranking
  const calculateMatchScore = (med, searchLower) => {
    let score = 0;
    const nameLower = med.name.toLowerCase();
    const chemicalLower = med.genericName ? med.genericName.toLowerCase() : '';
    
    // Exact match in name (highest priority)
    if (nameLower === searchLower) {
      score += 1000;
    }
    // Name starts with search text (high priority)
    else if (nameLower.startsWith(searchLower)) {
      score += 500;
      // Bonus for shorter names (more specific matches)
      score += Math.max(0, 100 - nameLower.length);
    }
    // Name contains search text
    else if (nameLower.includes(searchLower)) {
      score += 200;
      // Bonus for earlier position in name
      const position = nameLower.indexOf(searchLower);
      score += Math.max(0, 50 - position);
    }
    
    // Chemical name matches (lower priority than name matches)
    if (chemicalLower) {
      if (chemicalLower === searchLower) {
        score += 100;
      } else if (chemicalLower.startsWith(searchLower)) {
        score += 50;
      } else if (chemicalLower.includes(searchLower)) {
        score += 20;
      }
    }
    
    return score;
  };

  // Filter and sort medicines by relevance
  const filteredMedicines = (() => {
    const searchText = editingMedicineRowIndex !== null ? medicineSearchForEdit : medicineSearch;
    if (!searchText || searchText.trim().length === 0) {
      return [];
    }
    
    const searchLower = searchText.toLowerCase();
    
    // Filter medicines that match
    const matched = medicines.filter((med) => {
      const nameMatch = med.name.toLowerCase().includes(searchLower);
      const chemicalMatch = med.genericName && med.genericName.toLowerCase().includes(searchLower);
      return nameMatch || chemicalMatch;
    });
    
    // Sort by match score (highest first)
    return matched.sort((a, b) => {
      const scoreA = calculateMatchScore(a, searchLower);
      const scoreB = calculateMatchScore(b, searchLower);
      return scoreB - scoreA; // Descending order
    });
  })();

  useEffect(() => {
    const searchText = editingMedicineRowIndex !== null ? medicineSearchForEdit : medicineSearch;
    if (searchText && searchText.trim().length > 0 && filteredMedicines.length > 0) {
      setSelectedMedicineIndex(0);
    } else {
      setSelectedMedicineIndex(-1);
    }
    medicineRowRefs.current = [];
  }, [medicines.length, medicineSearch, medicineSearchForEdit, editingMedicineRowIndex, filteredMedicines.length]);

  // Update dropdown position when editing row changes
  useEffect(() => {
    if (editingMedicineRowIndex !== null && productRefs.current[editingMedicineRowIndex]) {
      setDropdownPosition(
        calculateDropdownPosition(productRefs.current[editingMedicineRowIndex])
      );
    }
  }, [editingMedicineRowIndex]);

  // Scroll selected row into view
  useEffect(() => {
    if (showMedicineList && selectedMedicineIndex >= 0 && medicineRowRefs.current[selectedMedicineIndex] && medicineDropdownRef.current) {
      const selectedRow = medicineRowRefs.current[selectedMedicineIndex];
      const dropdownContainer = medicineDropdownRef.current;
      
      const rowTop = selectedRow.offsetTop;
      const rowBottom = rowTop + selectedRow.offsetHeight;
      const containerTop = dropdownContainer.scrollTop;
      const containerBottom = containerTop + dropdownContainer.clientHeight;
      
      if (rowTop < containerTop) {
        dropdownContainer.scrollTop = rowTop - 10;
      } else if (rowBottom > containerBottom) {
        dropdownContainer.scrollTop = rowBottom - dropdownContainer.clientHeight + 10;
      }
    }
  }, [selectedMedicineIndex, showMedicineList]);

  const generateReturnNumber = (date = new Date()) => {
    return `PR${String(date.getFullYear()).slice(-2)}${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}${String(date.getDate()).padStart(2, '0')}${String(date.getHours()).padStart(2, '0')}`;
  };

  const handleKeyDown = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  // Handle medicine selection from search dropdown
  const handleSelectMedicineFromSearch = (medicine, targetRowIndex = null) => {
    if (!medicine) return;
    
    setMedicineSearch('');
    setShowMedicineList(false);
    setSelectedMedicineIndex(-1);
    setMedicineSearchForEdit('');
    setEditingMedicineRowIndex(null);
    
    // Store selected medicine for batch selection
    const selectedMedicine = medicine;
    
    const isEditing = targetRowIndex !== null && targetRowIndex !== products.length;
    const targetIndex = isEditing ? targetRowIndex : products.length;

    // Check if medicine has batches
    if (medicine.batches && medicine.batches.length > 1) {
      const currentProduct = targetIndex < products.length ? products[targetIndex] : null;
      const initialQtys = {};
      if (currentProduct?.batch && currentProduct?.qty) {
        initialQtys[currentProduct.batch] = currentProduct.qty;
      }
      setBatchModalInitialQtys(initialQtys);
      setSelectedMedicineForBatch(medicine);
      setCurrentProductIndex(targetIndex);
      setShowBatchModal(true);
    } else if (medicine.batches && medicine.batches.length === 1) {
      const selectedBatch = medicine.batches[0];
      handleMultiBatchSelection(targetIndex, medicine, [{ batch: selectedBatch, qty: '' }]);
    } else {
      // No batches available - add product without batch
      if (isEditing && targetIndex < products.length) {
        // Update existing row
        setProducts((prevProducts) =>
          prevProducts.map((product, i) =>
            i === targetIndex
              ? {
                  ...product,
                  productId: medicine.id,
                  productName: medicine.name,
                  batch: '',
                  expiry: '',
                  expiryDisplay: '',
                  qty: '',
                  supplierName: '',
                  supplierId: 0,
                  purchaseRefNo: '',
                  purchasePrice: 0,
                  taxCode: medicine.taxCode || '',
                  selectedMedicine: selectedMedicine,
                }
              : product
          )
        );
      } else {
        // Add new row
        setProducts((prevProducts) => [
          ...prevProducts,
          {
            productId: medicine.id,
            productName: medicine.name,
            batch: '',
            expiry: '',
            expiryDisplay: '',
            qty: '',
            supplierName: '',
            supplierId: 0,
            purchaseRefNo: '',
            purchasePrice: 0,
            taxCode: medicine.taxCode || '',
            selectedMedicine: selectedMedicine,
          }
        ]);
      }
    }
  };

  // Format expiry date to MM/YY
  const formatExpiryDisplay = (expiryDate) => {
    if (!expiryDate) return '';
    try {
      const date = new Date(expiryDate);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${year}`;
    } catch {
      return '';
    }
  };

  // Handle keyboard navigation in medicine dropdown
  const handleMedicineSearchKeyDown = (e) => {
    // Always show list when editing or if there are medicines
    if (editingMedicineRowIndex !== null || filteredMedicines.length > 0) {
      setShowMedicineList(true);
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredMedicines.length === 0) return;
      if (selectedMedicineIndex === -1) {
        setSelectedMedicineIndex(0);
      } else {
        setSelectedMedicineIndex((prev) => 
          prev < filteredMedicines.length - 1 ? prev + 1 : prev
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filteredMedicines.length === 0) return;
      if (selectedMedicineIndex === -1) {
        setSelectedMedicineIndex(filteredMedicines.length - 1);
      } else {
        setSelectedMedicineIndex((prev) => (prev > 0 ? prev - 1 : -1));
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedMedicineIndex >= 0 && selectedMedicineIndex < filteredMedicines.length) {
        const selectedMedicine = filteredMedicines[selectedMedicineIndex];
        if (selectedMedicine) {
          const targetIndex = editingMedicineRowIndex !== null ? editingMedicineRowIndex : products.length;
          handleSelectMedicineFromSearch(selectedMedicine, targetIndex);
        }
      } else if (filteredMedicines.length > 0 && selectedMedicineIndex === -1) {
        // If no item is selected but there are items, select the first one
        const selectedMedicine = filteredMedicines[0];
        if (selectedMedicine) {
          const targetIndex = editingMedicineRowIndex !== null ? editingMedicineRowIndex : products.length;
          handleSelectMedicineFromSearch(selectedMedicine, targetIndex);
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMedicineList(false);
      setMedicineSearch('');
      setMedicineSearchForEdit('');
      setEditingMedicineRowIndex(null);
      setSelectedMedicineIndex(-1);
      setMedicines([]);
    }
  };

  // Handle clicking on product name to edit
  const handleProductNameClick = (index, e) => {
    e.stopPropagation();
    setEditingMedicineRowIndex(index);
    setMedicineSearchForEdit('');
    setShowMedicineList(true);
    
    // Calculate dropdown position based on the clicked product name cell
    const clickedCell = e.currentTarget;
    if (clickedCell) {
      setDropdownPosition(calculateDropdownPosition(clickedCell));
    }
  };

  const buildProductRow = (medicine, batch, qty = '') => ({
    productId: medicine.id,
    productName: medicine.name,
    batch: batch.batchNo,
    expiry: batch.expiryDate || '',
    expiryDisplay: batch.expiryDate ? formatExpiryDisplay(batch.expiryDate) : '',
    supplierName: batch.supplierName || '',
    supplierId: batch.supplierId || 0,
    purchaseRefNo: batch.purchaseRefNo || '',
    purchasePrice: parseFloat(batch.purchasePrice || 0),
    taxCode: medicine.taxCode || batch.gstRate?.toString() || '',
    selectedMedicine: medicine,
    maxQty: batch.availableQty || 0,
    qty: qty !== '' && qty !== null && qty !== undefined ? String(qty) : '',
  });

  const handleMultiBatchSelection = (index, medicine, batchSelections) => {
    const newRows = batchSelections
      .filter(({ qty }) => qty === '' || parseFloat(qty || 0) > 0)
      .map(({ batch, qty }) => buildProductRow(medicine, batch, qty));

    if (newRows.length === 0) return;

    const isEditing = index !== null && index < products.length;

    if (isEditing) {
      setProducts((prevProducts) => {
        const before = prevProducts.slice(0, index);
        const after = prevProducts.slice(index + 1);
        return [...before, ...newRows, ...after];
      });
    } else {
      setProducts((prevProducts) => [...prevProducts, ...newRows]);
    }

    setTimeout(() => {
      if (medicineSearchInputRef.current) {
        medicineSearchInputRef.current.focus();
        setShowMedicineList(true);
        setSelectedMedicineIndex(0);
        if (medicineSearchContainerRef.current) {
          setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
        }
      }
    }, 100);
  };

  const handleExpiryInputChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');
    let formattedValue = '';

    if (numericValue.length >= 1) {
      formattedValue = numericValue[0];
    }
    if (numericValue.length >= 2) {
      formattedValue = numericValue.substring(0, 2);
    }
    if (numericValue.length >= 3) {
      formattedValue = `${numericValue.substring(0, 2)}/${numericValue.substring(2, 3)}`;
    }
    if (numericValue.length >= 4) {
      formattedValue = `${numericValue.substring(0, 2)}/${numericValue.substring(2, 4)}`;
    }

    let fullDate = '';
    if (numericValue.length === 4) {
      const month = numericValue.substring(0, 2);
      const year = `20${numericValue.substring(2, 4)}`;
      fullDate = `${year}-${month}-01`;
    }

    setProducts((prevProducts) =>
      prevProducts.map((product, i) =>
        i === index
          ? {
              ...product,
              expiry: fullDate,
              expiryDisplay: formattedValue,
            }
          : product
      )
    );
  };

  const handleExpiryKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (qtyRefs.current[index]) {
        qtyRefs.current[index].focus();
        qtyRefs.current[index].select();
      }
    }
  };

  const handleQtyChange = (index, value) => {
    const requestedQty = parseFloat(value || 0);
    const currentProduct = products[index];
    
    if (!currentProduct || !currentProduct.batch) {
      return;
    }

    const maxQty = parseFloat(currentProduct.maxQty || 0);
    
    // Validate quantity doesn't exceed available stock
    if (requestedQty > maxQty) {
      toast.warning(`Insufficient stock. Only ${maxQty} units available in this batch. Quantity set to ${maxQty}.`, {
        autoClose: 2000,
      });
      // Set to max available quantity
      setProducts((prevProducts) =>
        prevProducts.map((product, i) =>
          i === index
            ? {
                ...product,
                qty: maxQty,
              }
            : product
        )
      );
    } else {
      setProducts((prevProducts) =>
        prevProducts.map((product, i) =>
          i === index
            ? {
                ...product,
                qty: value,
              }
            : product
        )
      );
    }
  };

  const handleQtyKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Focus medicine search input in the new entry row
        setTimeout(() => {
        if (medicineSearchInputRef.current) {
          medicineSearchInputRef.current.focus();
          setShowMedicineList(true);
          setSelectedMedicineIndex(0);
          if (medicineSearchContainerRef.current) {
            setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
          }
          }
        }, 150);
    }
  };


  const handleDeleteRow = (index) => {
    setProducts((prevProducts) => {
      const updated = prevProducts.filter((_, i) => i !== index);
      return updated;
    });
  };

  const resetForm = () => {
    const now = new Date();
    setNote('');
    setProducts([]);
    setSelectedIndex(null);
    setReturnNumber(generateReturnNumber(now));
    setReturnDate(
      `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(
        2,
        '0'
      )}-${now.getFullYear()} ${now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })}`
    );

    setTimeout(() => {
      if (medicineSearchInputRef.current) {
        medicineSearchInputRef.current.focus();
      }
    }, 150);
  };

  // Handle ESC key when editing medicine
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && editingMedicineRowIndex !== null) {
        e.preventDefault();
        setShowMedicineList(false);
        setMedicineSearch('');
        setMedicineSearchForEdit('');
        setEditingMedicineRowIndex(null);
        setSelectedMedicineIndex(-1);
        setMedicines([]);
      }
    };

    if (editingMedicineRowIndex !== null) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [editingMedicineRowIndex]);

  // Handle click outside to close medicine dropdown when editing
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (editingMedicineRowIndex !== null) {
        if (
          medicineDropdownRef.current &&
          !medicineDropdownRef.current.contains(e.target) &&
          productRefs.current[editingMedicineRowIndex] &&
          !productRefs.current[editingMedicineRowIndex].contains(e.target)
        ) {
          setShowMedicineList(false);
          setMedicineSearch('');
          setMedicineSearchForEdit('');
          setEditingMedicineRowIndex(null);
          setSelectedMedicineIndex(-1);
          setMedicines([]);
        }
      }
    };

    if (editingMedicineRowIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingMedicineRowIndex]);

  const isRowValid = (row) =>
    row.productId &&
    row.batch.trim() &&
    row.expiryDisplay &&
    row.expiryDisplay.length === 5 &&
    row.qty &&
    parseFloat(row.qty) > 0 &&
    parseFloat(row.supplierId || 0) > 0;

  const handleSave = async () => {
    const itemsWithDetails = products.filter(
      (row) =>
        row.productId &&
        row.batch.trim() &&
        row.expiryDisplay &&
        row.expiryDisplay.length === 5 &&
        row.qty &&
        parseFloat(row.qty) > 0
    );

    const missingSupplier = itemsWithDetails.some((row) => parseFloat(row.supplierId || 0) <= 0);
    if (missingSupplier) {
      toast.error('Please select a supplier for each return item.', {
        autoClose: 2000,
      });
      return;
    }

    const validItems = products.filter(isRowValid);

    if (!validItems.length) {
      toast.error('Please add at least one product with batch, expiry, and supplier details.', {
        autoClose: 1500,
      });
      return;
    }

    // Build payload array with all item details
    const payload = validItems.map((item, index) => {
      // Calculate tax amount: (PurchasePrice * Qty) * (taxCode / 100)
      const purchasePrice = parseDecimal(item.purchasePrice || 0);
      const qty = parseDecimal(item.qty || 0);
      const taxCode = parseFloat(item.taxCode || 0); // Convert string to number
      const taxAmount = parseDecimal((purchasePrice * qty) * (taxCode / 100));
      
      return {
        SI_No: index + 1,
        ItemId: item.productId || 0,
        ItemName: item.productName || '',
        BatchNo: item.batch || '',
        BatchCode: item.batch || '',
        Qty: qty,
        ExpiryDate: item.expiry || '',
        ExpiryDisplay: item.expiryDisplay || '',
        SupplierName: item.supplierName || '',
        SupplierId: parseDecimal(item.supplierId || 0),
        PurchaseRefNo: item.purchaseRefNo || '',
        PurchasePrice: purchasePrice,
        TaxCode: item.taxCode || '',
        TaxAmount: taxAmount,
        DepartmentID : localStorage.getItem('department_id') || '',
      };
    });

    console.log('Purchase Return Payload:', payload);

    // Convert payload to JSON string
    //const payloadString = JSON.stringify(payload);
    //console.log('Purchase Return Payload (String):', payloadString);

    setLoading(true);

    try {
      const response = await axios.post(`${BASE_URL}${POST_PURCHASE_RETURN_DRAFT}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'sequence': localStorage.getItem('finac_year'),
          'invoiceNo': 0,
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success('Purchase return saved successfully.', { autoClose: 1000 });
        resetForm();
      } else {
        throw new Error('Failed to save purchase transaction');
      }
    } catch (error) {
      console.error('Failed to save purchase transaction:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.Messages ||
        'An error occurred while saving the purchase.';
      toast.error(errorMessage, { autoClose: 1500 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='flex h-full bg-background'>
      {/* <LeftMenu /> */}
      <ToastContainer
        position="top-center"
        autoClose={1500}
        limit={2}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      {loading && <Spinner />}

      <div className='flex-1 flex h-full flex-col overflow-hidden hide-scrollbar'>

        <div className="bg-white p-3 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Purchase Return Entry
              </h1>
            </div>
          </div>
        </div>

        <div className="bg-white ml-3 mr-3 rounded-lg shadow-sm p-3">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                <FiPackage className="text-indigo-600" size={18} />
                <h3 className="font-semibold text-black-800 text-sm uppercase tracking-wide">
                  Entry Information
                </h3>
              </div>

              <div className="grid grid-cols-12 gap-4 mt-4">

                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-semibold text-black-600 uppercase mb-1">
                    Return Date
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 bg-gray-50 rounded-lg text-sm font-medium"
                    value={returnDate}
                    readOnly
                  />
                </div>
              </div>
        </div>

        <div className="mt-3 flex-1 overflow-hidden flex flex-col ml-3 mr-3">
          <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-300">
            <h3 className="font-semibold text-black text-sm">Product Selection</h3>
              </div>

          <div className="overflow-x-auto rounded-lg border border-gray-400 flex-1 w-full">
            <div className="overflow-y-auto overflow-x-auto border rounded-xl relative bg-white h-full">
              <table className="border-collapse text-xs w-full" style={{ minWidth: '900px' }}>
                <thead className="bg-gray-200 text-left sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '50px' }}>No.</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '330px' }}>Product</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '120px' }}>Batch No</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '100px' }}>Expiry (MM/YY)</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Qty</th>
                    <th className="px-3 py-2.5 text-sm font-medium text-black border border-gray-300 whitespace-nowrap" style={{ minWidth: '80px' }}>Action</th>
                    </tr>
                  </thead>
                <tbody className="bg-white">
                  {/* Regular rows with products */}
                    {products.map((product, index) => (
                    <React.Fragment key={index}>
                      <tr 
                        className={`border-b border-gray-300 ${
                          selectedIndex === index
                            ? "bg-indigo-50" 
                            : "hover:bg-gray-50"
                        } transition-colors`}
                        onClick={() => setSelectedIndex(index)}
                      >
                        <td className="px-3 py-1 text-sm text-black border border-gray-200 whitespace-nowrap">
                          {index + 1}
                        </td>
                        
                        <td 
                          className="px-3 py-1 text-xs text-black font-medium border border-gray-200 whitespace-nowrap cursor-pointer hover:bg-indigo-50 transition-colors"
                          onClick={(e) => handleProductNameClick(index, e)}
                            ref={(el) => (productRefs.current[index] = el)}
                          title="Click to change product"
                        >
                          {product.productName || '-'}
                        </td>

                        <td 
                          className="px-3 py-1 border border-gray-200 relative cursor-pointer hover:bg-indigo-50 transition-colors"
                          onClick={() => {
                            if (product.productId && product.selectedMedicine) {
                              const initialQtys = {};
                              if (product.batch && product.qty) {
                                initialQtys[product.batch] = product.qty;
                              }
                              setBatchModalInitialQtys(initialQtys);
                              setSelectedMedicineForBatch(product.selectedMedicine);
                              setCurrentProductIndex(index);
                              setShowBatchModal(true);
                            }
                          }}
                          title={product.productId ? "Click to change batch" : "Select a product first"}
                        >
                          <input
                            type="text"
                            className="w-full px-1 py-1 text-black bg-white text-center font-medium text-xs cursor-pointer"
                            placeholder="Select batch"
                            value={product.batch || ''}
                            readOnly
                            disabled={!product.productId}
                          />
                        </td>

                        <td className="px-3 py-1 border border-gray-200">
                          <input
                            type="text"
                            className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-center font-medium text-xs"
                            placeholder="MM/YY"
                            value={product.expiryDisplay}
                            maxLength={5}
                            onChange={(e) => handleExpiryInputChange(index, e.target.value)}
                            ref={(el) => (expiryRefs.current[index] = el)}
                            onKeyDown={(e) => handleExpiryKeyDown(e, index)}
                            onFocus={(e) => e.target.select()}
                            disabled={!product.productId}
                          />
                        </td>

                        <td className="px-3 py-1 border border-gray-200">
                          <input
                            type="text"
                            className="w-full px-1 py-1 text-black bg-white focus:ring-1 focus:ring-indigo-200 focus:border-indigo-500 transition-colors text-right font-medium text-xs"
                            placeholder="0"
                            value={product.qty}
                            onChange={(e) => handleQtyChange(index, e.target.value)}
                            ref={(el) => (qtyRefs.current[index] = el)}
                            onKeyDown={(e) => handleQtyKeyDown(e, index)}
                            onFocus={(e) => e.target.select()}
                            disabled={!product.productId || !product.batch}
                          />
                        </td>
                        
                        <td className="px-3 py-1 border border-gray-200">
                          <button
                            type="button"
                            className="p-0.5 bg-red-50 text-red-500 rounded hover:bg-red-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(index);
                            }}
                            disabled={products.length === 1}
                            title="Remove item"
                          >
                            <MdDelete size={12} />
                          </button>
                        </td>
                      </tr>
                      {/* Supplier and Purchase Ref Info Row */}
                      {product.batch && product.qty && parseFloat(product.qty) > 0 && (
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <td colSpan="2" className="px-3 py-1 text-xs text-black border border-gray-200">
                            {product.supplierName ? (
                              <span>
                                <span className="font-medium">Supplier:</span> {product.supplierName}
                              </span>
                            ) : (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <span className="font-medium whitespace-nowrap">Supplier:</span>
                                <div className="flex-1 min-w-[180px]">
                                  <Select
                                    options={supplierOptions}
                                    placeholder="Select supplier"
                                    isSearchable
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    value={
                                      product.supplierId
                                        ? supplierOptions.find((opt) => opt.value === product.supplierId) || null
                                        : null
                                    }
                                    onChange={(selectedOption) => handleSupplierChange(index, selectedOption)}
                                    styles={customSelectStyles}
                                    noOptionsMessage={() => 'No suppliers found'}
                                  />
                                </div>
                              </div>
                            )}
                          </td>
                          <td colSpan="3" className="px-3 py-1 text-xs text-black border border-gray-200">
                            <span className="font-medium">Purchase Ref:</span> {product.purchaseRefNo || '-'}
                            {!product.purchaseRefNo && parseFloat(product.supplierId || 0) > 0 && (
                              <span className="text-gray-400 ml-2">(No purchase data)</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {/* New entry row - gray background */}
                  <tr className="bg-gray-200 relative">
                    <td className="px-3 py-1 text-xs text-black border border-gray-200 whitespace-nowrap">
                      {products.length + 1}
                    </td>

                    <td className="px-3 py-1 relative border border-gray-200">
                      <div className="relative w-full" ref={medicineSearchContainerRef}>
                        <input
                          className="w-full px-1 py-2.5 text-black focus:ring-1 focus:ring-yellow-400 text-xs"
                          value={medicineSearch}
                          onChange={(e) => {
                            const searchValue = e.target.value;
                            setMedicineSearch(searchValue);
                            setShowMedicineList(true);
                            
                            if (medicineSearchDebounceRef.current) {
                              clearTimeout(medicineSearchDebounceRef.current);
                            }
                            
                            if (searchValue && searchValue.length >= 1) {
                              medicineSearchDebounceRef.current = setTimeout(() => {
                                getInvoiceItems(searchValue);
                              }, 300);
                            } else if (searchValue.length === 0) {
                              setMedicines([]);
                            }
                            
                            if (searchValue && searchValue.trim().length > 0) {
                              setSelectedMedicineIndex(0);
                            } else {
                              setSelectedMedicineIndex(-1);
                            }
                            
                            if (medicineSearchContainerRef.current) {
                              setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                            }
                          }}
                          onFocus={() => {
                            setShowMedicineList(true);
                            setSelectedMedicineIndex(-1);
                            if (medicineSearchContainerRef.current) {
                              setDropdownPosition(calculateDropdownPosition(medicineSearchContainerRef.current));
                            }
                          }}
                          onKeyDown={handleMedicineSearchKeyDown}
                          onBlur={() => {
                            if (editingMedicineRowIndex === null) {
                              setTimeout(() => setShowMedicineList(false), 200);
                            }
                          }}
                          ref={medicineSearchInputRef}
                          placeholder="Search product..."
                        />
                      </div>
                    </td>

                    <td className="px-3 py-1 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1 border border-gray-200">
                      <input className="w-full border px-1 py-0.5 text-xs" disabled />
                    </td>
                    <td className="px-3 py-1 border border-gray-200"></td>
                  </tr>
                  </tbody>
                </table>
            </div>
              </div>
            </div>

        <div className="bg-white ml-3 mr-3 mt-3 mb-2 rounded-lg shadow-sm border border-gray-300">
          <div className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {/* Return Number */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-black">Return No:</span>
                <span className="text-sm font-bold text-black">{returnNumber}</span>
              </div>

              {/* Items Count */}
              <div className="flex items-center gap-2 border-l border-r border-gray-300 px-4">
                <span className="text-xs font-semibold text-black">Items:</span>
                <span className="text-base font-bold text-indigo-600">{products.filter(isRowValid).length}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-sm rounded-md hover:from-indigo-700 hover:to-purple-700 shadow-sm transition-all"
                  onClick={handleSave} 
                  disabled={isSubmitting}
                  ref={submitBtnRef}
                >
                  <FiSave size={16} className="inline mr-2" />
                  Submit
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-indigo-600 text-white font-medium text-sm rounded-md hover:bg-indigo-700 shadow-sm transition-all"
                  onClick={resetForm}
                >
                  New +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Selection Modal */}
      <BatchSelectionModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          setSelectedMedicineForBatch(null);
          setCurrentProductIndex(null);
          setBatchModalInitialQtys({});
        }}
        product={selectedMedicineForBatch}
        batches={selectedMedicineForBatch?.batches || []}
        initialBatchQtys={batchModalInitialQtys}
        onSelectBatches={(batchSelections) => {
          if (selectedMedicineForBatch && currentProductIndex !== null) {
            handleMultiBatchSelection(currentProductIndex, selectedMedicineForBatch, batchSelections);
            setShowBatchModal(false);
            setSelectedMedicineForBatch(null);
            setCurrentProductIndex(null);
            setBatchModalInitialQtys({});
          }
        }}
      />

      {/* Medicine Selection Dropdown Portal */}
      {(showMedicineList || editingMedicineRowIndex !== null) && createPortal(
        <>
          {editingMedicineRowIndex !== null && (
            <div
              className="fixed inset-0 z-[99998] bg-transparent"
              onClick={() => {
                setShowMedicineList(false);
                setMedicineSearch('');
                setMedicineSearchForEdit('');
                setEditingMedicineRowIndex(null);
                setSelectedMedicineIndex(-1);
                setMedicines([]);
              }}
            />
          )}
          <div
            ref={medicineDropdownRef}
            className="fixed bg-white shadow-2xl border z-[99999] max-h-96 overflow-auto"
            style={{
              top: dropdownPosition.isAbove ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.isAbove ? `${dropdownPosition.bottom}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              width: `${Math.max(dropdownPosition.width || 1000, 1000)}px`,
            }}
            onMouseDown={(e) => e.preventDefault()}
            tabIndex={-1}
          >
            {editingMedicineRowIndex !== null && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Search product..."
                  value={medicineSearchForEdit}
                  onChange={(e) => {
                    const searchValue = e.target.value;
                    setMedicineSearchForEdit(searchValue);
                    
                    if (medicineSearchDebounceRef.current) {
                      clearTimeout(medicineSearchDebounceRef.current);
                    }
                    
                    if (searchValue && searchValue.length >= 1) {
                      medicineSearchDebounceRef.current = setTimeout(() => {
                        getInvoiceItems(searchValue);
                      }, 300);
                    } else if (searchValue.length === 0) {
                      setMedicines([]);
                    }
                    
                    if (searchValue && searchValue.trim().length > 0) {
                      setSelectedMedicineIndex(0);
                    } else {
                      setSelectedMedicineIndex(-1);
                    }
                  }}
                  onKeyDown={handleMedicineSearchKeyDown}
                  autoFocus
                />
              </div>
            )}
            {filteredMedicines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
                  <thead className="bg-gray-200 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '50px' }}>SI</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '200px' }}>Name</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '80px' }}>QTY</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-medium text-black whitespace-nowrap" style={{ minWidth: '150px' }}>Company</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredMedicines.map((med, medIndex) => {
                      const totalQty = med.batches.reduce((sum, batch) => sum + (batch.availableQty || 0), 0);
                      const isSelected = medIndex === selectedMedicineIndex;
                      return (
                        <tr
                          key={medIndex}
                          ref={(el) => {
                            if (el) {
                              medicineRowRefs.current[medIndex] = el;
                            }
                          }}
                          className={`border-b border-gray-200 cursor-pointer ${
                            isSelected 
                              ? 'bg-indigo-200 hover:bg-indigo-300' 
                              : 'hover:bg-blue-100'
                          } transition-colors`}
                          style={{
                            backgroundColor: isSelected ? '#c7d2fe' : undefined
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedMedicineIndex(medIndex);
                            const targetIndex = editingMedicineRowIndex !== null ? editingMedicineRowIndex : products.length;
                            handleSelectMedicineFromSearch(med, targetIndex);
                          }}
                          onMouseEnter={() => setSelectedMedicineIndex(medIndex)}
                        >
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{medIndex + 1}</td>
                          <td className="px-4 py-2 border border-gray-200 font-semibold text-black whitespace-nowrap">{med.name}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{totalQty}</td>
                          <td className="px-4 py-2 border border-gray-200 text-black whitespace-nowrap">{med.manufacturer || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {filteredMedicines.length === 0 && (
              <div className="p-4 text-center text-black">
                <p>No products found</p>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default PurchaseReturn;

